import { Trait } from "utils/types"

export interface FeatureEntry {
  name: string
  value: string
}

export type FeatureFilters = Record<string, string>

export interface FeatureFacetValue {
  value: string
  count: number
}

export interface FeatureFacet {
  name: string
  values: FeatureFacetValue[]
}

interface TokenWithFeatures {
  features?: Record<string, unknown> | null
}

const ATTRIBUTE_QUERY_PARAM = "attributes"
const LEGACY_FEATURE_QUERY_PARAM = "features"

export const normalizeFeatureValue = (value: unknown) => {
  if (value === null || value === undefined) return undefined

  if (typeof value === "string") {
    const trimmed = value.trim()
    return trimmed && trimmed !== "All" ? trimmed : undefined
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value)
  }

  return undefined
}

export const featuresRecordToEntries = (features?: Record<string, unknown> | null): FeatureEntry[] =>
  Object.entries(features || {})
    .map(([name, value]) => {
      const normalizedValue = normalizeFeatureValue(value)
      return normalizedValue ? { name: name.trim(), value: normalizedValue } : null
    })
    .filter((feature): feature is FeatureEntry => Boolean(feature?.name && feature.value))

export const traitsToFeatureEntries = (traits?: Trait[] | null): FeatureEntry[] =>
  traits
    ?.map((trait) => {
      const normalizedTrait = normalizeFeatureValue(trait.value)
      if (!normalizedTrait) return null

      const [name, ...valueParts] = normalizedTrait.split(":")
      const value = normalizeFeatureValue(valueParts.join(":").trim())
      return value ? { name: name.trim(), value } : null
    })
    .filter((feature): feature is FeatureEntry => Boolean(feature?.name && feature.value)) || []

export const buildFeatureFacets = (tokens: TokenWithFeatures[]): FeatureFacet[] => {
  const facets = new Map<string, Map<string, number>>()

  tokens.forEach((token) => {
    featuresRecordToEntries(token.features).forEach(({ name, value }) => {
      const values = facets.get(name) || new Map<string, number>()
      values.set(value, (values.get(value) || 0) + 1)
      facets.set(name, values)
    })
  })

  return Array.from(facets.entries())
    .map(([name, values]) => ({
      name,
      values: Array.from(values.entries())
        .map(([value, count]) => ({ value, count }))
        .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value)),
    }))
    .sort((a, b) => a.name.localeCompare(b.name))
}

export const hasFeatureFilters = (filters?: FeatureFilters) => Boolean(filters && Object.keys(filters).length > 0)

export const coerceFeatureFilterValue = (value: string) => {
  if (value === "true") return true
  if (value === "false") return false

  const numericValue = Number(value)
  if (Number.isFinite(numericValue) && String(numericValue) === value) return numericValue

  return value
}

export const filtersToFeatureJson = (filters: FeatureFilters) =>
  Object.entries(filters).reduce<Record<string, string | number | boolean>>((json, [name, value]) => {
    if (name && value) json[name] = coerceFeatureFilterValue(value)
    return json
  }, {})

export const encodeFeatureFilters = (filters: FeatureFilters) =>
  JSON.stringify(
    Object.entries(filters).reduce<Record<string, { type: "enum"; options: Record<string, boolean> }>>(
      (attributes, [name, value]) => {
        if (name && value) attributes[name] = { type: "enum", options: { [value]: true } }
        return attributes
      },
      {}
    )
  )

export const decodeFeatureFilters = (searchParams: URLSearchParams): FeatureFilters => {
  const raw = searchParams.get(ATTRIBUTE_QUERY_PARAM) || searchParams.get(LEGACY_FEATURE_QUERY_PARAM)
  if (!raw) return {}

  try {
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {}

    return Object.entries(parsed).reduce<FeatureFilters>((filters, [name, value]) => {
      if (typeof value === "string") {
        const normalizedValue = normalizeFeatureValue(value)
        if (normalizedValue) filters[name] = normalizedValue
        return filters
      }

      if (value && typeof value === "object" && !Array.isArray(value)) {
        const options = (value as { options?: Record<string, boolean> }).options
        const selectedValue = Object.entries(options || {}).find(([, selected]) => selected)?.[0]
        const normalizedValue = normalizeFeatureValue(selectedValue)
        if (normalizedValue) filters[name] = normalizedValue
      }

      return filters
    }, {})
  } catch {
    return {}
  }
}

export const updateSearchParamsWithFeatureFilters = (
  searchParams: URLSearchParams,
  filters: FeatureFilters
) => {
  const nextParams = new URLSearchParams(searchParams)
  nextParams.delete(LEGACY_FEATURE_QUERY_PARAM)

  if (hasFeatureFilters(filters)) {
    nextParams.set(ATTRIBUTE_QUERY_PARAM, encodeFeatureFilters(filters))
  } else {
    nextParams.delete(ATTRIBUTE_QUERY_PARAM)
  }

  return nextParams
}

export const toggleFeatureFilter = (
  filters: FeatureFilters,
  feature: FeatureEntry
) => {
  const nextFilters = { ...filters }

  if (nextFilters[feature.name] === feature.value) {
    delete nextFilters[feature.name]
  } else {
    nextFilters[feature.name] = feature.value
  }

  return nextFilters
}

export const getFeatureFilterHref = (baseHref: string, feature: FeatureEntry) => {
  const separator = baseHref.includes("?") ? "&" : "?"
  return `${baseHref}${separator}${ATTRIBUTE_QUERY_PARAM}=${encodeURIComponent(
    encodeFeatureFilters({ [feature.name]: feature.value })
  )}`
}
